import React, { useState } from 'react'
import SEO from '../components/SEO'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    emne: 'Generel henvendelse',
    beskrivelse: '',
    website_url: '', // Antispam honeypot
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [isAdvisoryExpanded, setIsAdvisoryExpanded] = useState(false)

  function handleChange(e) {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  function validate() {
    const errs = {}
    if (!formData.navn.trim()) {
      errs.navn = 'Navn skal udfyldes'
    }
    if (!formData.email.trim()) {
      errs.email = 'E-mailadresse skal udfyldes'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errs.email = 'Indtast venligst en gyldig e-mail'
    }
    if (!formData.telefon.trim()) {
      errs.telefon = 'Telefonnummer skal udfyldes'
    } else if (formData.telefon.replace(/\D/g, '').length < 8) {
      errs.telefon = 'Telefonnummer skal indeholde mindst 8 cifre'
    }
    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = 'Beskrivelse / besked skal udfyldes'
    }
    return errs
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitError('')

    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          website_url: formData.website_url,
          form_type: 'Kontaktformular',
          navn: formData.navn,
          telefon: formData.telefon,
          email: formData.email,
          emne: formData.emne,
          beskrivelse: formData.beskrivelse,
          timestamp: new Date().toISOString(),
        }),
      })

      if (!res.ok) {
        throw new Error(`Serverfejl (${res.status})`)
      }

      setSubmitted(true)
    } catch (err) {
      console.error('Contact form submit error:', err)
      setSubmitError(
        'Der skete en fejl under indsendelsen. Ring venligst direkte til os på 21 43 81 49.'
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SEO
        title="Kontakt – Kryger Wet Blaster"
        description="Kontakt Krüger Wet Blaster ApS. Erhvervsvej 1, 6650 Brørup. Tlf: (+45) 21 43 81 49, email: jonkrygerpetersen@gmail.com."
      />

      {/* Header Banner */}
      <section className="bg-slate-900 text-white border-b border-slate-800 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
              Kontakt os vi vil gerne hjælpe jer.
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Kontakt os og få et uforpligtende snak om hvad vi kan hjælpe dig med.
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: Contact Details & Info (5 cols) */}
          <div className="lg:col-span-5 flex flex-col">
            {/* Contact Card with Jon Krüger photo */}
            <div className="bg-white rounded-2xl border border-slate-200/90 overflow-hidden shadow-sm flex flex-col h-full">
              <div className="relative aspect-4/3 w-full bg-slate-100 overflow-hidden shrink-0">
                <img
                  src="/cdn-cgi/image/width=800,quality=85,format=auto/_assets/2500px-9484.jpg"
                  alt="Jon Krüger – Krüger Wet Blaster"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/20 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5 text-white">
                  <h3 className="text-xl font-bold leading-tight">Jon Krüger</h3>
                  <p className="text-xs text-sky-300 font-medium">Indehaver & rådgiver · Krüger Wet Blaster ApS</p>
                </div>
              </div>

              <div className="p-6 space-y-5 text-slate-900 flex-1 flex flex-col justify-between">
                <div className="space-y-3.5 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Direkte telefon</div>
                      <a
                        href="tel:+4521438149"
                        className="text-slate-900 hover:text-sky-600 font-semibold transition-colors"
                      >
                        (+45) 21 43 81 49
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500 font-medium">E-mail</div>
                      <a
                        href="mailto:jonkrygerpetersen@gmail.com"
                        className="text-slate-900 hover:text-sky-600 font-medium truncate block transition-colors"
                      >
                        jonkrygerpetersen@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 pt-3 border-t border-slate-100">
                    <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div className="text-slate-700 text-sm">
                      <div className="text-xs text-slate-500 font-medium">Adresse & Værksted</div>
                      <span className="font-semibold text-slate-900">Krüger Wet Blaster ApS</span>
                      <br />
                      Erhvervsvej 1, 6650 Brørup
                      <br />
                      <span className="text-xs text-slate-500">CVR: 41047216</span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200/80 text-xs text-slate-600 flex items-start gap-2.5">
                    <i className="fa-solid fa-circle-info text-sky-600 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>
                      Har du spørgsmål eller ønsker du at se en maskine i funktion? Du er altid velkommen til at ringe eller kigge forbi efter aftale.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Contact Form (7 cols) */}
          <div className="lg:col-span-7 flex flex-col">
            <div className="bg-white rounded-2xl border border-slate-200/90 p-6 sm:p-8 shadow-sm flex flex-col h-full">
              <h2 className="text-xl font-bold text-slate-900 mb-2">Send en besked</h2>
              <p className="text-xs sm:text-sm text-slate-500 mb-6">
                Udfyld kontaktformularen, så vender vi tilbage hurtigst muligt – typisk inden for 24 timer.
              </p>

              {submitted ? (
                <div className="py-10 text-center space-y-4 bg-slate-50 rounded-xl p-8 border border-slate-200">
                  <div className="text-emerald-600 text-4xl mx-auto">
                    <i className="fa-solid fa-circle-check" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">Beskeden er sendt!</h3>
                  <p className="text-sm text-slate-600 max-w-md mx-auto">
                    Mange tak for din henvendelse. Vi har modtaget din besked og kontakter dig snarest.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false)
                      setFormData({
                        navn: '',
                        telefon: '',
                        email: '',
                        emne: 'Generel henvendelse',
                        beskrivelse: '',
                        website_url: '',
                      })
                    }}
                    className="inline-flex items-center gap-2 bg-sky-600 text-white font-semibold text-xs px-4 py-2 rounded hover:bg-sky-500 transition-colors"
                  >
                    Send endnu en besked
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-4">
                    {/* Name */}
                  <div className="space-y-1">
                    <label htmlFor="navn" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="navn"
                      name="navn"
                      value={formData.navn}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                        errors.navn ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {errors.navn && <p className="text-xs text-red-600">{errors.navn}</p>}
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="din@email.dk"
                        className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                          errors.email ? 'border-red-500' : 'border-slate-300'
                        }`}
                      />
                      {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                    </div>

                    <div className="space-y-1">
                      <label htmlFor="telefon" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="telefon"
                        name="telefon"
                        value={formData.telefon}
                        onChange={handleChange}
                        placeholder="F.eks. 21 43 81 49"
                        className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                          errors.telefon ? 'border-red-500' : 'border-slate-300'
                        }`}
                      />
                      {errors.telefon && <p className="text-xs text-red-600">{errors.telefon}</p>}
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="space-y-1">
                    <label htmlFor="emne" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                      Hvad drejer henvendelsen sig om?
                    </label>
                    <select
                      id="emne"
                      name="emne"
                      value={formData.emne}
                      onChange={handleChange}
                      className="w-full bg-transparent border-0 border-b border-slate-300 rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 font-medium"
                    >
                      <option value="Generel henvendelse">Generel henvendelse / rådgivning</option>
                      <option value="Tilbud på vådblæsekabine">Tilbud på vådblæsekabine (KWB-8060 / 12080)</option>
                      <option value="Tørisblæsning">Tørisblæsningsudstyr (Kärcher)</option>
                      <option value="Blæsemidler & Kemi">Blæsemidler & kemi (SuperShine, Glasball, Rustoff m.m.)</option>
                      <option value="Reservedele & Tilbehør">Reservedele & service</option>
                      <option value="Besøg på vores virksomhed">Ønske om besøg på virksomheden</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div className="space-y-1">
                    <label htmlFor="beskrivelse" className="block text-xs font-bold uppercase tracking-wider text-slate-700">
                      Beskrivelse / besked <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="beskrivelse"
                      name="beskrivelse"
                      rows={5}
                      value={formData.beskrivelse}
                      onChange={handleChange}
                      placeholder="Skriv hvad du har af udfordringer eller spørgsmål..."
                      className={`w-full bg-transparent border-0 border-b rounded-none px-0 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-sky-600 focus:ring-0 ${
                        errors.beskrivelse ? 'border-red-500' : 'border-slate-300'
                      }`}
                    />
                    {errors.beskrivelse && <p className="text-xs text-red-600">{errors.beskrivelse}</p>}
                  </div>

                  {/* Honeypot field */}
                  <div className="sr-only absolute -left-[9999px]">
                    <label htmlFor="contact_website_url">Efterlad tomt</label>
                    <input
                      type="text"
                      id="contact_website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                    {submitError && (
                      <div className="p-3 rounded-md bg-red-50 text-red-700 text-xs border border-red-200">
                        {submitError}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 active:bg-sky-700 disabled:opacity-50 text-white font-bold text-sm py-3.5 px-6 rounded-lg shadow-sm transition-all cursor-pointer mt-4"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin text-xs" aria-hidden="true" />
                        <span>Sender besked...</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        <span>Send henvendelse</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Google Maps View */}
        <div className="mt-10 w-full h-80 sm:h-96 rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm relative bg-slate-100">
          <iframe
            title="Krüger Wet Blaster ApS placering"
            src="https://maps.google.com/maps?q=Erhvervsvej%201,%206650%20Br%C3%B8rup&t=&z=14&ie=UTF8&iwloc=&output=embed"
            className="w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </>
  )
}
